import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { TeamChatTypes } from '@frontend/api-team-chat';
import { ErrorBoundary } from '@frontend/error-boundary';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { ContentLoader, SpinningLoader, useAlert, useDebouncedFn, useModalControl } from '@frontend/design-system';
import { INITIAL_MESSAGE_ITEM_INDEX } from '../../constants';
import { useActiveConversationSelector } from '../../providers/active-conversation.provider';
import { useTeamChatSelector } from '../../providers/team-chat.provider';
import type { Target } from '../../utils';
import { MessageComposer } from '../composer';
import { EmptyConversation } from './empty-conversation';
import { DeleteMessageConfirmation } from './message/actions-bar-popover/delete-message-confirmation';
import { MessageRegularVirtuoso } from './message/message-regular-virtuoso';

type Props = {
  selectedTarget: Target | undefined;
  messages: TeamChatTypes.Message[];
  fetchNextPage?: () => void;
  fetchPreviousPage?: () => void;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  isFetching?: boolean;
};

export const ConversationBody = ({
  selectedTarget,
  messages,
  fetchNextPage,
  fetchPreviousPage,
  hasNextPage,
  hasPreviousPage,
  isFetching = false,
}: Props) => {
  // TODO: there is probably a better way to do this. it's a little jumpy, but it is a start
  const [height, setHeight] = useState(72);
  const { t } = useTranslation('team-chat');
  const alert = useAlert();
  const isFocused = useTeamChatSelector((ctx) => ctx.isFocused);
  const isThread = useTeamChatSelector((ctx) => ctx.isThread);
  const highlightedMessageId = useTeamChatSelector((ctx) => ctx.highlightedMessageId);
  const conversation = useActiveConversationSelector((ctx) => ctx.conversation);
  const members = useActiveConversationSelector((ctx) => ctx.members);
  const getFirstUnreadMessageIndex = useActiveConversationSelector((ctx) => ctx.getFirstUnreadMessageIndex);
  const sendConversationEvent = useActiveConversationSelector((ctx) => ctx.sendConversationEvent);
  const deleteMessage = useActiveConversationSelector((ctx) => ctx.deleteMessage);
  const editMessage = useActiveConversationSelector((ctx) => ctx.editMessage);
  const { modalProps, openModal } = useModalControl();
  const [currentDeleteMessageId, setCurrentDeleteMessageId] = useState<string>();
  const [currentEditMessageId, setCurrentEditMessageId] = useState<string>();

  const currentDeleteMessage = useMemo(() => {
    return messages.find((message) => message.id === currentDeleteMessageId);
  }, [currentDeleteMessageId, messages]);

  // This function is already memoized based on anything that might change its output
  const listRef = useRef<VirtuosoHandle>(null);

  // firstItemIndex is kept as state so it only moves when items are *prepended*.
  const [firstItemIndex, setFirstItemIndex] = useState(INITIAL_MESSAGE_ITEM_INDEX);
  // Guards against re-initializing firstItemIndex for the same session.
  const sessionFirstItemRef = useRef('');
  // Captures messagesToRender.length just before fetchPreviousPage so the effect
  // below can compute the exact number of items that were prepended.
  const prependLengthRef = useRef<number | null>(null);
  // One-shot guard: prevents the unread scroll from re-firing on every prepend.
  // Reset when the session (channel / highlight target) changes.
  const hasScrolledToUnreadRef = useRef(false);

  const messageUnreadIndex = useMemo(() => {
    return getFirstUnreadMessageIndex();
  }, [getFirstUnreadMessageIndex]);

  const [unreadIndex, setUnreadIndex] = useState(-1);
  const shouldShowMessages = useMemo(
    () => !(messageUnreadIndex === -1 && !!conversation?.unreadCount),
    [messageUnreadIndex, conversation?.unreadCount]
  );

  const channelIdRef = useRef<string | undefined>(conversation?.channelId);

  /** Fetches the next page of messages when the user scrolls up */
  useEffect(() => {
    if (!conversation || !messages || !messages.length) {
      return;
    }
    if (messageUnreadIndex === -1 && !!conversation.unreadCount) {
      fetchNextPage?.();
    } else {
      const hasLastReadMessageIndex = !!conversation.lastReadMessageId;
      const indexMismatch = messageUnreadIndex !== -1 && messageUnreadIndex !== messages.length - 1;
      const shouldSetIndex = unreadIndex === -1 || channelIdRef.current !== conversation.channelId || !isFocused;
      if (shouldSetIndex && (indexMismatch || !hasLastReadMessageIndex)) {
        setUnreadIndex(messageUnreadIndex ?? -1);
      }
      if (channelIdRef.current !== conversation.channelId) {
        channelIdRef.current = conversation.channelId;
      }
    }
  }, [messageUnreadIndex, messages.length, conversation?.unreadCount, conversation?.channelId, !isFocused]);

  const markConversationAsRead = useDebouncedFn(sendConversationEvent, 1000);

  useEffect(() => {
    if (
      !conversation ||
      !messages.length ||
      hasNextPage ||
      (!hasNextPage &&
        (conversation.unreadCount === 0 || conversation.lastReadMessageId === messages[messages.length - 1]?.id)) ||
      !isFocused ||
      !shouldShowMessages
    ) {
      return;
    }

    /* 
        Mark messages read only if the last read messageid does not matches 
        In case of a thread, we do ge a last read message id but can point to a normal message or a thread
        we are not sure. hence we add another check that messages.length>1 so that when a thead is opened we mark it 
        read
      */
    markConversationAsRead({
      conversationId: conversation.channelId,
      event: 'read',
      ...(isThread && messages.length > 1 && { thread_id: messages[0]?.id }),
    });
  }, [markConversationAsRead, shouldShowMessages, isFocused, isThread, conversation, messages, hasNextPage]);

  useLayoutEffect(() => {
    if (!shouldShowMessages && conversation?.unreadCount) {
      return;
    }
    setTimeout(() => {
      if (
        !hasScrolledToUnreadRef.current && // only scroll once per session; the ref is reset when the channel/target changes
        !highlightedMessageId && // skip when navigating to a specific message (it handles its own scroll)
        unreadIndex > -1 && // a valid unread boundary exists in the current message list
        (conversation?.unreadCount ?? 0) > 0 && // there are actually unread messages to scroll to
        isFocused && // don't disturb the viewport when the panel is in the background
        firstItemIndex !== INITIAL_MESSAGE_ITEM_INDEX // not yet initialised — wait until firstItemIndex reflects real data
      ) {
        const virtualUnreadIndex = firstItemIndex + unreadIndex;
        listRef.current?.scrollToIndex({ index: virtualUnreadIndex, align: 'center', behavior: 'smooth' });
        hasScrolledToUnreadRef.current = true;
      }
    }, 300);
  }, [conversation?.unreadCount, shouldShowMessages, unreadIndex, isFocused, highlightedMessageId, firstItemIndex]);

  /** The new line should only show up for up to 5 seconds */
  const timer = useRef<NodeJS.Timeout | undefined>();
  const [isNewLineExpired, setIsNewLineExpired] = useState(false);
  useEffect(() => {
    if (isFocused && !isNewLineExpired) {
      timer.current = setTimeout(() => {
        setIsNewLineExpired(true);
      }, 5000);
      return () => clearTimeout(timer.current);
    } else {
      clearTimeout(timer.current);
      return;
    }
  }, [isFocused, isNewLineExpired]);

  const messagesToRender = useMemo(
    () =>
      messages.filter((message) => {
        const hasReplies = message.replyCount > 0;
        const isDeleted = message.type === 'deleted';
        const shouldShowDeleted = isDeleted && hasReplies;
        const shouldShow = !isDeleted || shouldShowDeleted;
        // we need to filter here rather than at any level lower in the react tree,
        // because otherwise virtuoso will render zero-sized elements,
        // which issues a warning in the console
        return shouldShow;
      }),
    [messages]
  );

  const highlightedPhysicalIdx = useMemo(
    () => (highlightedMessageId ? messagesToRender.findIndex((m) => m.id === highlightedMessageId) : -1),
    [highlightedMessageId, messagesToRender]
  );

  // Stable key that drives both the Virtuoso remount (re-reads initialTopMostItemIndex)
  // and the firstItemIndex reset below.
  const virtuosoKey = `${conversation?.channelId ?? ''}:${highlightedMessageId ?? ''}`;

  // When the channel or navigation target changes, reset firstItemIndex so the init
  // effect below can reinitialize it for the new session's data.
  useEffect(() => {
    setFirstItemIndex(INITIAL_MESSAGE_ITEM_INDEX);
    prependLengthRef.current = null;
    hasScrolledToUnreadRef.current = false;
  }, [virtuosoKey]);

  // Initialize firstItemIndex once per session after the new data finishes loading.
  useEffect(() => {
    if (sessionFirstItemRef.current === virtuosoKey) return;
    if (messagesToRender.length === 0 || isFetching) return;
    sessionFirstItemRef.current = virtuosoKey;
    setFirstItemIndex(INITIAL_MESSAGE_ITEM_INDEX - messagesToRender.length);
  }, [virtuosoKey, messagesToRender.length, isFetching]);

  // When fetchPreviousPage resolves, compute the exact prepended count and decrement.
  useEffect(() => {
    if (prependLengthRef.current === null) return;
    if (isFetching) return;
    const addedCount = messagesToRender.length - prependLengthRef.current;
    if (addedCount > 0) {
      setFirstItemIndex((prev) => prev - addedCount);
    }
    prependLengthRef.current = null;
  }, [messagesToRender.length, isFetching]);

  const showDeletionModal = useCallback((messageId: string) => {
    setCurrentDeleteMessageId(messageId);
    openModal();
  }, []);

  const onDelete = useCallback(
    (type: 'hard' | 'soft' = 'soft') => {
      if (!currentDeleteMessageId) {
        return;
      }
      return deleteMessage({ messageId: currentDeleteMessageId, hardDelete: type === 'hard' })
        .then((message) => {
          if (isThread) {
            const parentMessage = messages.find((m) => m.id === message.parentId);
            const replySender = message.userId;
            if (parentMessage && parentMessage.type !== 'deleted') {
              return editMessage({
                ...parentMessage,
                threadParticipantIds: parentMessage.threadParticipantIds.filter(
                  (participantId) => participantId !== replySender
                ),
              }).catch((e) => {
                console.error(e);
                return message;
              });
            }
            return message;
          } else {
            return Promise.resolve(message);
          }
        })
        .then(() => {
          alert.success(t('Message deleted successfully'));
        })
        .catch((e) => {
          alert.error(t('Failed to delete message'));
          throw new Error('Unable to delete message.', e);
        });
    },
    [messages, deleteMessage, close, currentDeleteMessageId, isThread]
  );

  return (
    <ErrorBoundary>
      {conversation && shouldShowMessages && messages.length ? (
        <Virtuoso
          key={virtuosoKey}
          data={messagesToRender}
          style={{ height: isThread ? height : '100%' }}
          // scrolling UP fetches older messages (prepend) — fetchPreviousPage uses id_lt cursor
          // Gate on hasPreviousPage so we don't set prependLengthRef and call the API
          // when already at the very start of the channel (which would leave the ref
          // stuck and miscount every subsequent live append).
          startReached={
            hasPreviousPage
              ? () => {
                  prependLengthRef.current = messagesToRender.length;
                  fetchPreviousPage?.();
                }
              : undefined
          }
          endReached={hasNextPage && fetchNextPage ? () => fetchNextPage() : undefined}
          firstItemIndex={firstItemIndex}
          // When a highlighted message is present, open at its position; otherwise open at the
          // bottom (newest message). Because Virtuoso is remounted via key this is always respected.
          initialTopMostItemIndex={highlightedPhysicalIdx !== -1 ? highlightedPhysicalIdx : messagesToRender.length}
          alignToBottom={!highlightedMessageId}
          ref={listRef}
          totalListHeightChanged={(height: number) => {
            setHeight(height);
          }}
          itemContent={(index, message, { firstItemIndex: contextFirstItemIndex }) => {
            const currentIndex = index - contextFirstItemIndex;
            return (
              <MessageRegularVirtuoso
                message={message}
                isHighlighted={highlightedMessageId === message.id}
                isEditing={currentEditMessageId === message.id}
                onStartEditing={() => {
                  setCurrentEditMessageId(message.id);
                }}
                onCancelEditing={() => {
                  setCurrentEditMessageId(undefined);
                }}
                showNewLine={!isNewLineExpired && (conversation?.unreadCount ?? 0) > 0 && unreadIndex === currentIndex}
                onDelete={showDeletionModal}
                isThread={isThread}
                isThreadFirstMessage={isThread && !message.parentId}
              />
            );
          }}
          components={{
            Header: ({ context }: { context?: { isFetching: boolean; firstItemIndex: number } }) =>
              context?.isFetching ? (
                <div css={styles.spinner}>
                  <SpinningLoader size='xs' />
                </div>
              ) : null,
          }}
          context={{ isFetching, firstItemIndex }}
          // Disable auto-follow when navigating to a specific message, or when there are
          // still newer pages to load (historical slice) so new messages don't pull the
          // viewport away from the user's current position.
          followOutput={highlightedMessageId || hasNextPage ? false : 'auto'}
        />
      ) : (
        <EmptyConversation memberCount={members.length || 0} type={conversation?.type ?? 'DM'} />
      )}
      <ErrorBoundary>{<MessageComposer type='new' selectedTarget={selectedTarget} />}</ErrorBoundary>
      <ContentLoader show={isFetching} message={t('Getting latest messages')} />
      {currentDeleteMessage && (
        <DeleteMessageConfirmation
          message={currentDeleteMessage}
          modalProps={modalProps}
          onConfirm={() => onDelete(currentDeleteMessage.type === 'reply' ? 'hard' : 'soft')}
        />
      )}
    </ErrorBoundary>
  );
};

ConversationBody.displayName = 'ConversationBody';

const styles = {
  spinner: css({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(1),
    width: '100%',
  }),
};
